// src/components/Dashboard/widgets/ITPHoldPointsWidget.tsx
// Widget 9 — ITP Hold Points Pending
// Open-count comes from MOCK_DASHBOARD.metrics (backbone pipeline aggregate).
// Cleared-count comes from getAllHoldPointClearances() (localStorage + seeds).
// Click header → ITP module. Click cleared row → ITP at that WI reference.
import { ClipboardCheck, ChevronRight, CheckCircle, AlertCircle } from 'lucide-react'
import { getAllHoldPointClearances } from '../../../utils/wiStepCompletion'
import { MOCK_DASHBOARD } from '../../../utils/mockData'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function ITPHoldPointsWidget({ onNavigate }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const clearances  = getAllHoldPointClearances()
  const clearedCount = clearances.length

  // Open hold-point count from backbone pipeline metrics
  const openCount = MOCK_DASHBOARD.metrics?.openHoldPoints ?? 0

  // Show most recent clearances as "just done" context
  const recentClears = [...clearances]
    .sort((a, b) => b.clearedAt.localeCompare(a.clearedAt))
    .slice(0, 3)

  const hasPending = openCount > 0

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('itp')}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <ClipboardCheck className="w-4 h-4 text-teal-600" />
          <span className="font-semibold text-gray-900 text-sm">ITP hold points</span>
          {hasPending && (
            <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
              <AlertCircle className="w-2.5 h-2.5" />{openCount} pending
            </span>
          )}
          {!hasPending && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
              All clear
            </span>
          )}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 divide-x border-b bg-gray-50/60">
        <div className="px-4 py-2.5 text-center">
          <div className={`text-xl font-bold ${hasPending ? 'text-amber-600' : 'text-gray-300'}`}>
            {openCount}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">Awaiting witness</div>
        </div>
        <div className="px-4 py-2.5 text-center">
          <div className="text-xl font-bold text-green-600">{clearedCount}</div>
          <div className="text-xs text-gray-400 mt-0.5">Cleared this period</div>
        </div>
      </div>

      {/* Pending banner */}
      {hasPending && (
        <div
          className="mx-5 mt-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => navigate('itp', { openTab: 'pending' })}
        >
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {openCount} hold point{openCount !== 1 ? 's' : ''} pending witness sign-off
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              Construction cannot proceed past these points until cleared.
            </p>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
        </div>
      )}

      {/* Recent clearances */}
      {recentClears.length > 0 && (
        <div className="px-5 pt-4 pb-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">
            Recently cleared
          </p>
          <div className="space-y-2">
            {recentClears.map((c, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2.5 p-2.5 bg-green-50/60 rounded-lg border border-green-100 cursor-pointer hover:bg-green-100 transition-colors"
                onClick={() => navigate('itp')}
              >
                <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{c.holdPointRef}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {c.wiReference} · cleared by {c.clearedByName}
                    {c.witnessName ? ` · witnessed by ${c.witnessName}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {openCount === 0 && clearedCount === 0 && (
        <div className="px-5 py-8 text-center">
          <ClipboardCheck className="w-6 h-6 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No ITP hold point data yet</p>
        </div>
      )}

      <div className="px-5 py-2.5 border-t mt-auto">
        <button onClick={() => navigate('itp')} className="text-xs text-blue-600 hover:underline">
          Open ITP module →
        </button>
      </div>
    </div>
  )
}
